import { Box } from '@mui/material';
import React from 'react';
import TableCellEditor, { CELL_STYLE_SX } from './TableCellEditor';
import TableLineMenu from './TableLineMenu';
import { cellText, resolveCellStyle } from './tableModel';
import type { AnchorCell } from './tableModel';
import type { useTableEditor } from './useTableEditor';

type Editor = ReturnType<typeof useTableEditor>;

const isSelected = (rect: Editor['selectionRect'], a: AnchorCell) =>
  !!rect &&
  a.r + a.rowSpan - 1 >= rect.r1 &&
  a.r <= rect.r2 &&
  a.c + a.colSpan - 1 >= rect.c1 &&
  a.c <= rect.c2;

interface TableGridRowProps {
  editor: Editor;
  r: number;
  rowAnchors: AnchorCell[];
  onPasteTable: (html: string, text: string) => boolean;
  onPasteImage: (blob: Blob) => void;
  onStep: (r: number, c: number, delta: number) => void;
}

/** One table row: its left-gutter menu plus the anchor cells that start in it. */
export default function TableGridRow({
  editor,
  r,
  rowAnchors,
  onPasteTable,
  onPasteImage,
  onStep,
}: TableGridRowProps) {
  return (
    <tr>
      <td style={{ padding: 0, verticalAlign: 'top' }}>
        <TableLineMenu
          orientation="row"
          index={r}
          canDelete={editor.rowCount > 1}
          onInsertBefore={() => editor.addRow(r)}
          onInsertAfter={() => editor.addRow(r + 1)}
          onDelete={() => editor.removeRow(r)}
          onStyle={(style) => editor.setRowStyle(r, style)}
        />
      </td>
      {rowAnchors.map((a) => {
        const styleKind = resolveCellStyle(a.cell, r);
        return (
          <Box
            component="td"
            key={a.cell.id}
            data-cell={`${a.r}-${a.c}`}
            colSpan={a.colSpan}
            rowSpan={a.rowSpan}
            sx={{
              border: '1px solid #e0e0e0',
              verticalAlign: 'top',
              bgcolor: CELL_STYLE_SX[styleKind].bgcolor,
            }}
          >
            <TableCellEditor
              html={cellText(a.cell)}
              styleKind={styleKind}
              selected={isSelected(editor.selectionRect, a)}
              ariaLabel={`Cell row ${a.r + 1} column ${a.c + 1}`}
              onCommit={(html) => editor.updateCell(a.r, a.c, html)}
              onFocusCell={() => editor.setFocus([a.r, a.c])}
              onSelectStart={() => {
                editor.setAnchor([a.r, a.c]);
                editor.setFocus([a.r, a.c]);
              }}
              onSelectExtend={() => editor.setFocus([a.r, a.c])}
              onKeyNav={(event) => {
                if (event.key !== 'Tab') return;
                event.preventDefault();
                onStep(a.r, a.c, event.shiftKey ? -1 : 1);
              }}
              onPasteTable={onPasteTable}
              onPasteImage={onPasteImage}
            />
          </Box>
        );
      })}
    </tr>
  );
}
